import { CheckCircle2, Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';

interface Substep {
  id: string;
  step_number: number;
  title: string;
  isCompleted: boolean;
}

interface MainStep {
  id: string;
  step_number: number;
  title: string;
  isCompleted: boolean;
  isCurrent: boolean;
  substeps?: Substep[];
}

interface StepsSidebarProps {
  mainSteps: MainStep[];
  currentMainStepIndex: number;
  currentSubstepId: string | null;
  beginnerMode: boolean;
  onMainStepClick: (index: number) => void;
  onSubstepClick?: (substepId: string) => void;
}

/**
 * Compact sidebar showing workflow steps.
 * Designed to live inside a flex container — no fixed positioning.
 * Uses minimal vertical space per step for quick scanning and navigation.
 */
export const StepsSidebar = ({
  mainSteps,
  currentMainStepIndex,
  currentSubstepId,
  beginnerMode,
  onMainStepClick,
  onSubstepClick,
}: StepsSidebarProps) => {
  const { t } = useTranslation();

  const completedCount = mainSteps.filter(step => step.isCompleted).length;
  const totalCount = mainSteps.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Check if any substeps are currently expanded (beginner mode + current step has substeps)
  const currentStep = mainSteps[currentMainStepIndex];
  const hasExpandedSubsteps = beginnerMode && currentStep?.substeps && currentStep.substeps.length > 0;

  return (
    <div className="flex h-full flex-col">
      {/* Header — compact progress summary */}
      <div className="flex-none px-3 py-2.5 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-1.5">
          <h2 className="font-semibold text-sm">{t('workflow.workflowSteps')}</h2>
          <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-primary/10 text-primary border-primary/20">
            {pct}%
          </Badge>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">
          {completedCount}/{totalCount} {t('workflow.completed')}
        </div>
      </div>

      {/* Steps List — equal distribution when no substeps expanded, natural + scroll when expanded */}
      <div className={cn(
        'flex-1 overflow-y-auto',
        !hasExpandedSubsteps && 'flex flex-col'
      )}>
        {mainSteps.map((step, index) => {
          const isCurrentMain = index === currentMainStepIndex;
          const hasSubsteps = beginnerMode && step.substeps && step.substeps.length > 0;

          return (
            <div
              key={step.id}
              className={cn(!hasExpandedSubsteps && 'flex flex-col min-h-0')}
              style={!hasExpandedSubsteps ? { flex: '1 1 0' } : undefined}
            >
              {/* Main Step */}
              <button
                onClick={() => onMainStepClick(index)}
                style={!hasExpandedSubsteps ? { minHeight: '36px', maxHeight: '72px' } : undefined}
                className={cn(
                  'w-full text-left px-3 flex items-center gap-2 transition-colors',
                  !hasExpandedSubsteps ? 'flex-1' : 'py-2',
                  'hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                  isCurrentMain && 'bg-primary/10 border-l-2 border-l-primary',
                  !isCurrentMain && 'border-l-2 border-l-transparent',
                  step.isCompleted && !isCurrentMain && 'bg-success/5'
                )}
              >
                {/* Status icon */}
                <div className="flex-none">
                  {step.isCompleted ? (
                    <CheckCircle2 className="h-4 w-4 text-success" />
                  ) : (
                    <Circle className={cn(
                      'h-4 w-4',
                      isCurrentMain ? 'text-primary' : 'text-muted-foreground/50'
                    )} />
                  )}
                </div>

                {/* Step number */}
                <span
                  className={cn(
                    'flex-none text-[10px] font-mono font-bold w-5 h-5 flex items-center justify-center rounded',
                    isCurrentMain
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {step.step_number}
                </span>

                {/* Title — single line, truncated */}
                <span
                  className={cn(
                    'flex-1 text-sm truncate',
                    isCurrentMain ? 'font-semibold text-foreground' : 'text-foreground/70'
                  )}
                >
                  {step.title}
                </span>

                {/* Expand/collapse indicator for substeps */}
                {hasSubsteps && (
                  <div className="flex-none">
                    {isCurrentMain ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </div>
                )}
              </button>

              {/* Substeps — shown only when expanded (beginner mode + current) */}
              {hasSubsteps && isCurrentMain && (
                <div className="ml-5 border-l border-muted">
                  {step.substeps!.map((substep) => {
                    const isCurrentSubstep = substep.id === currentSubstepId;

                    return (
                      <button
                        key={substep.id}
                        onClick={() => onSubstepClick?.(substep.id)}
                        className={cn(
                          'w-full text-left pl-4 pr-3 py-1.5 flex items-center gap-2 transition-colors',
                          'hover:bg-accent/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary',
                          isCurrentSubstep && 'bg-primary/5 border-l-2 border-l-primary/50 -ml-px',
                          substep.isCompleted && !isCurrentSubstep && 'bg-success/5'
                        )}
                      >
                        <div className="flex-none">
                          {substep.isCompleted ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                          ) : (
                            <Circle className={cn(
                              'h-3.5 w-3.5',
                              isCurrentSubstep ? 'text-primary' : 'text-muted-foreground/40'
                            )} />
                          )}
                        </div>
                        <span
                          className={cn(
                            'flex-1 text-xs truncate',
                            isCurrentSubstep ? 'font-medium text-foreground' : 'text-foreground/60'
                          )}
                        >
                          {substep.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
